/**
 * <merch-module> — a homepage destination's product row, machine-aware.
 *
 * With a machine in the garage, the row is that machine's collection filtered
 * by the module's query; without one, the fallback collection's best sellers.
 * Rows come through ProductRow (Section Rendering, retries, a session cache).
 * An empty row hides; a module with neither tiles nor a row hides itself, so
 * the required order of the remaining modules is kept without a gap.
 *
 * With a machine, the row is the whole module: the destination's generic
 * tiles hide, so the customer sees their machine's parts and not those plus
 * a general set beneath (Lucas, 2026-10-09). They return when the machine's
 * row comes back empty, or the garage is cleared.
 *
 * Re-runs on garage:changed, and a slow response for an earlier machine can
 * never land on top of a newer one: each run gets its own token.
 */
(function () {
  'use strict';

  class MerchModule extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;
      this.row = this.querySelector('[data-merch-row]');
      this.tiles = this.querySelector('.merch-module__tiles');
      this.onGarage = () => this.update();
      document.addEventListener('garage:changed', this.onGarage);
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('garage:changed', this.onGarage);
    }

    /** The URL to render for the current state, or null for no row. */
    rowUrl() {
      const active = window.Garage && window.Garage.active();
      const query = this.dataset.machineQuery || '';
      if (active) {
        if (!query) return null;
        return `/collections/${encodeURIComponent(active.key)}?${query}&sort_by=best-selling&sections=product-row`;
      }
      const fallback = this.dataset.fallbackUrl;
      if (!fallback) return null;
      return `${fallback}?sort_by=best-selling&sections=product-row`;
    }

    async update() {
      if (!this.row || !window.ProductRow) {
        this.reflect();
        return;
      }
      const url = this.rowUrl();
      const token = (this.token = (this.token || 0) + 1);
      if (!url) {
        this.row.hidden = true;
        this.showTiles(true);
        this.reflect();
        return;
      }

      const active = window.Garage && window.Garage.active();
      const title = this.row.querySelector('[data-merch-row-title]');
      if (title) {
        const template = active ? (this.dataset.rowHeadingMachine || 'For your [machine]') : (this.dataset.rowHeading || 'Top sellers');
        title.textContent = template.replace('[machine]', active ? active.label : '');
      }
      const link = this.row.querySelector('[data-row-link]');
      if (link) link.href = url.replace(/[?&]sections=product-row/, '').replace(/[?&]sort_by=best-selling/, '');

      const body = this.row.querySelector('[data-row-body]');
      if (body) body.setAttribute('aria-busy', 'true');
      this.row.hidden = false;

      let count = 0;
      try {
        const html = await window.ProductRow.fetch(url);
        if (token !== this.token) return; // superseded by a newer machine
        count = window.ProductRow.paint(this.row, html);
      } catch {
        if (token !== this.token) return;
        count = window.ProductRow.paint(this.row, '');
      }
      this.row.hidden = count === 0;
      // The machine's row stands alone; the generic tiles are the fallback.
      this.showTiles(!active || count === 0);
      if (!this.row.hidden) this.row.toggleAttribute('data-machine-scope', !!active);
      this.reflect();
    }

    showTiles(show) {
      if (this.tiles) this.tiles.hidden = !show;
    }

    /** Hide the whole module when it has nothing to show. */
    reflect() {
      const hasTiles = !!this.querySelector('.merch-module__tiles li');
      const hasRow = !!this.row && !this.row.hidden;
      this.hidden = !hasTiles && !hasRow;
    }
  }

  if (!customElements.get('merch-module')) customElements.define('merch-module', MerchModule);
})();
